import PropTypes from 'prop-types';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFreezeScroll, useSmartPositioning } from '@/shared/hooks';
import { useOutsideClick } from '@/shared/hooks/useOutsideClick';
import './dropdown.scss';

const Dropdown = ({
   trigger = null,
   icon = null,
   text = '',
   leftIcon = null,
   rightIcon = null,
   items = [],
   position = 'auto',
   className = '',
   buttonClassName = '',
   dropdownClassName = '',
   withPadding = true,
   onOpenChange = () => {},
   scrollContainerId = '',
   disabled = false,
   offsetTop = 0,
   offsetLeft = 0,
}) => {
   const [open, setOpen] = useState(false);

   const triggerRef = useRef(null);
   const dropdownRef = useRef(null);

   const preferredPosition = ['center', 'auto'].includes(position) ? null : position;

   const { pickerStyles, positionName } = useSmartPositioning({
      open,
      triggerRef,
      containerRef: dropdownRef,
      preferred: preferredPosition,
   });

   useFreezeScroll(scrollContainerId, open);

   const toggleDropdown = e => {
      if (disabled) return;
      e.stopPropagation();
      setOpen(prev => {
         const next = !prev;
         onOpenChange?.(next);
         return next;
      });
   };

   const closeDropdown = React.useCallback(() => {
      setOpen(false);
      onOpenChange?.(false);
   }, [onOpenChange]);

   const handleItemClick = action => {
      action?.();
      closeDropdown();
   };

   useOutsideClick(dropdownRef, e => {
      if (triggerRef.current?.contains(e.target)) return;
      closeDropdown();
   });

   // Close dropdown on scroll
   React.useEffect(() => {
      if (!open) return;

      const handleScroll = () => {
         closeDropdown();
      };

      // Listen to scroll events on window and all scrollable parents
      window.addEventListener('scroll', handleScroll, true);

      return () => {
         window.removeEventListener('scroll', handleScroll, true);
      };
   }, [open, closeDropdown]);

   const [centerStyle, setCenterStyle] = useState({});

   useLayoutEffect(() => {
      if (open && position === 'center' && triggerRef.current) {
         const rect = triggerRef.current.getBoundingClientRect();
         setCenterStyle({
            top: `${rect.bottom + window.scrollY + offsetTop}px`,
            left: `${rect.left + rect.width / 2 + offsetLeft}px`,
            transform: 'translateX(-50%)',
         });
      }
   }, [open, position, offsetTop, offsetLeft]);

   const renderTriggerContent = () => {
      if (trigger) return trigger;

      return (
         <>
            {leftIcon && <span className="dropdown-icon icon-left ct-icon">{leftIcon}</span>}
            {icon && !text && !leftIcon && !rightIcon && <span className="dropdown-icon ct-icon">{icon}</span>}
            {text && <span className="dropdown-text">{text}</span>}
            {rightIcon && <span className="dropdown-icon icon-right ct-icon">{rightIcon}</span>}
         </>
      );
   };

   const buttonClasses = [
      'dropdown-trigger',
      withPadding ? 'with-padding' : 'no-padding',
      open ? 'active dropdown-open' : '',
      disabled ? 'disabled' : '',
      buttonClassName,
   ]
      .filter(Boolean)
      .join(' ');

   const dropdownClasses = ['dropdown-menu', `pos-${positionName || 'auto'}`, dropdownClassName]
      .filter(Boolean)
      .join(' ');

   return (
      <>
         <div className={`dropdown-wrapper ct-relative ${className} ${open ? 'open' : ''}`}>
            <button
               ref={triggerRef}
               onClick={toggleDropdown}
               className={buttonClasses}
               type="button"
               disabled={disabled}
            >
               {renderTriggerContent()}
            </button>
         </div>

         {open &&
            createPortal(
               <div
                  ref={dropdownRef}
                  className={dropdownClasses}
                  style={{
                     ...pickerStyles,
                     ...centerStyle,
                     ...(position !== 'center' && {
                        top: pickerStyles.top ? `calc(${pickerStyles.top} + ${offsetTop}px)` : undefined,
                        left: pickerStyles.left ? `calc(${pickerStyles.left} + ${offsetLeft}px)` : undefined,
                     }),
                  }}
               >
                  {items.map(item => {
                     const key = item.id || item.value || item.label;

                     if (React.isValidElement(item)) {
                        return React.cloneElement(item, { key, onClick: closeDropdown });
                     }

                     if (typeof item === 'function') {
                        const ItemComp = item;
                        return <ItemComp key={key} onClick={closeDropdown} />;
                     }

                     return (
                        <button
                           key={key}
                           className={`dropdown-item ct-flex ct-aiCenter ct-gap8 ${item?.className || ''}`}
                           onClick={e => {
                              e.stopPropagation();
                              handleItemClick(item.action || item.formatter);
                           }}
                           disabled={item?.disabled}
                        >
                           {item.icon && <span className="dropdown-item-icon ct-icon">{item.icon}</span>}
                           {item.label && <span className="dropdown-item-label ct-flex1">{item.label}</span>}
                        </button>
                     );
                  })}
               </div>,
               document.body,
            )}
      </>
   );
};

Dropdown.propTypes = {
   trigger: PropTypes.node,
   icon: PropTypes.node,
   text: PropTypes.string,
   leftIcon: PropTypes.node,
   rightIcon: PropTypes.node,
   items: PropTypes.arrayOf(
      PropTypes.oneOfType([
         PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            icon: PropTypes.node,
            label: PropTypes.string,
            action: PropTypes.func,
            formatter: PropTypes.func,
            className: PropTypes.string,
            disabled: PropTypes.bool,
         }),
         PropTypes.node,
         PropTypes.func,
      ]),
   ),
   position: PropTypes.oneOf(['auto', 'left', 'right', 'top', 'bottom', 'center']),
   className: PropTypes.string,
   buttonClassName: PropTypes.string,
   dropdownClassName: PropTypes.string,
   withPadding: PropTypes.bool,
   onOpenChange: PropTypes.func,
   scrollContainerId: PropTypes.string,
   disabled: PropTypes.bool,
   offsetTop: PropTypes.number,
   offsetLeft: PropTypes.number,
};

export default Dropdown;
